/**
 * Enhanced Error Boundary Component
 * Provides comprehensive error handling for React components with recovery options
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '../utils/logger';
import { errorHandler, ApplicationError, ErrorType, ErrorSeverity } from '../utils/errorHandler';
import '../styles/errorBoundary.css';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
  enableRecovery?: boolean;
  component?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorCount: number;
  lastErrorTime: number;
  isRecovering: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private retryTimeouts: NodeJS.Timeout[] = [];
  private readonly maxRetries = 3;
  private readonly errorResetTime = 10000; // 10 seconds

  constructor(props: ErrorBoundaryProps) {
    super(props);
    
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
      lastErrorTime: 0,
      isRecovering: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, component } = this.props;
    const { errorCount, lastErrorTime } = this.state;

    // Log the error
    logger.error('React error boundary caught error', {
      error: error.message,
      componentStack: errorInfo.componentStack,
      component
    });

    // Report to error handler
    const appError = new ApplicationError(
      error.message,
      ErrorType.SYSTEM,
      ErrorSeverity.HIGH,
      {
        context: {
          component: component || 'Unknown',
          operation: 'React render',
          metadata: {
            componentStack: errorInfo.componentStack,
            errorBoundary: true
          }
        },
        technicalDetails: {
          error,
          errorInfo
        }
      }
    );

    errorHandler.handleError(appError);

    // Check if this is a recurring error
    const now = Date.now();
    const isRecurringError = now - lastErrorTime < this.errorResetTime;
    const newErrorCount = isRecurringError ? errorCount + 1 : 1;

    this.setState({
      errorInfo,
      errorCount: newErrorCount,
      lastErrorTime: now
    });

    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }

    // Auto-recovery attempt for non-critical errors
    if (this.props.enableRecovery && newErrorCount <= this.maxRetries && !this.isCriticalError(error)) {
      this.scheduleRecovery();
    }
  }

  componentWillUnmount() {
    // Clear any pending recovery timeouts
    this.retryTimeouts.forEach(timeout => clearTimeout(timeout));
  }

  private isCriticalError(error: Error): boolean {
    // Determine if error is critical based on error characteristics
    const errorMessage = error.message.toLowerCase();
    const criticalPatterns = [
      'cannot read property',
      'cannot access',
      'is not a function',
      'maximum update depth exceeded',
      'infinite loop'
    ];

    return criticalPatterns.some(pattern => errorMessage.includes(pattern));
  }

  private scheduleRecovery = () => {
    const { errorCount } = this.state;
    const delay = Math.min(1000 * Math.pow(2, errorCount - 1), 10000); // Exponential backoff, max 10s

    logger.info(`Scheduling error recovery attempt ${errorCount}/${this.maxRetries} in ${delay}ms`);

    this.setState({ isRecovering: true });

    const timeout = setTimeout(() => {
      this.handleReset();
    }, delay);

    this.retryTimeouts.push(timeout);
  };

  private handleReset = () => {
    logger.info('Attempting to recover from error');
    
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isRecovering: false
    });

    // Clear completed timeout
    this.retryTimeouts = this.retryTimeouts.filter(t => t !== undefined);
  };

  private handleManualReset = () => {
    logger.info('Manual error recovery initiated');
    
    // Clear any pending automatic recovery
    this.retryTimeouts.forEach(timeout => clearTimeout(timeout));
    this.retryTimeouts = [];
    
    this.handleReset();
  };

  private handleReload = () => {
    window.location.reload();
  };

  private renderErrorDetails() {
    const { error, errorInfo } = this.state;
    const { showDetails } = this.props;

    if (!showDetails || !error || !errorInfo) return null;

    return (
      <details className="error-boundary-details">
        <summary>Error Details</summary>
        <div className="error-boundary-details-content">
          <h4>Error Message:</h4>
          <pre>{error.toString()}</pre>
          
          <h4>Stack Trace:</h4>
          <pre>{error.stack}</pre>
          
          <h4>Component Stack:</h4>
          <pre>{errorInfo.componentStack}</pre>
        </div>
      </details>
    );
  }

  private renderRecoveryOptions() {
    const { enableRecovery } = this.props;
    const { errorCount, isRecovering } = this.state;

    return (
      <div className="error-boundary-actions">
        {enableRecovery && errorCount <= this.maxRetries && (
          <button 
            onClick={this.handleManualReset}
            disabled={isRecovering}
            className="error-boundary-button primary"
          >
            {isRecovering ? 'Recovering...' : 'Try Again'}
          </button>
        )}
        
        <button 
          onClick={this.handleReload}
          className="error-boundary-button secondary"
        >
          Reload Page
        </button>

        {process.env.NODE_ENV === 'development' && (
          <button
            onClick={() => {
              const logs = localStorage.getItem('app_logs');
              const blob = new Blob([logs || ''], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `error-logs-${Date.now()}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="error-boundary-button tertiary"
          >
            Download Logs
          </button>
        )}
      </div>
    );
  }

  render() {
    const { hasError, error, errorCount, isRecovering } = this.state;
    const { children, fallback } = this.props;

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback) {
        return <>{fallback}</>;
      }

      // Default error UI
      return (
        <div className="error-boundary-container">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>

            <h1 className="error-boundary-title">
              {errorCount > 1 ? 'Error Persists' : 'Something went wrong'}
            </h1>

            <p className="error-boundary-message">
              {isRecovering
                ? 'Attempting to recover automatically...'
                : errorCount > this.maxRetries
                ? 'The application encountered a critical error. Please reload the page.'
                : 'We encountered an unexpected error. You can try again or reload the page.'}
            </p>

            {errorCount > 1 && errorCount <= this.maxRetries && (
              <p className="error-boundary-retry-info">
                Attempt {errorCount} of {this.maxRetries}
              </p>
            )}

            {this.renderRecoveryOptions()}
            {this.renderErrorDetails()}

            <div className="error-boundary-support">
              <p>If the problem persists, please contact support with the following reference:</p>
              <code className="error-boundary-reference">
                {new Date().toISOString()}-{Math.random().toString(36).substr(2, 9)}
              </code>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

/**
 * HOC for wrapping components with error boundary
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps} component={Component.displayName || Component.name}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
}

/**
 * Hook for programmatic error handling
 */
export function useErrorHandler() {
  return React.useCallback((error: Error, errorInfo?: ErrorInfo) => {
    const appError = new ApplicationError(
      error.message,
      ErrorType.SYSTEM,
      ErrorSeverity.HIGH,
      {
        context: {
          component: 'Hook',
          operation: 'User error',
          metadata: errorInfo ? { componentStack: errorInfo.componentStack } : undefined
        }
      }
    );

    errorHandler.handleError(appError);
  }, []);
}